// src/components/booking/ReviewModal.jsx
import { useState } from "react";
import {
  collection, addDoc, updateDoc, doc,
  serverTimestamp, query, where, getDocs,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import { X, Star } from "lucide-react";

export default function ReviewModal({ booking, tenantId, existingReview, onClose, onSuccess }) {
  const [rating,  setRating]  = useState(existingReview?.rating  || 0);
  const [hover,   setHover]   = useState(0);
  const [comment, setComment] = useState(existingReview?.comment || "");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");

  const isEdit = Boolean(existingReview?.id);

  async function handleSubmit(e) {
    e.preventDefault();
    if (rating === 0) { setError("Please select a star rating."); return; }
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await updateDoc(doc(db, "reviews", existingReview.id), {
          rating,
          comment: comment.trim(),
          updatedAt: serverTimestamp(),
        });
      } else {
        // Prevent duplicate review for same booking
        const existing = await getDocs(
          query(collection(db, "reviews"), where("bookingId", "==", booking.id))
        );
        if (!existing.empty) {
          setError("You've already reviewed this appointment.");
          return;
        }
        await addDoc(collection(db, "reviews"), {
          tenantId,
          bookingId:    booking.id,
          userId:       booking.userId,
          staffId:      booking.barberId || booking.barber?.id || null,
          staffName:    booking.barber?.name || null,
          serviceName:  booking.service?.name || null,
          customerName: booking.customer?.name || null,
          rating,
          comment:      comment.trim(),
          status:       "published",
          createdAt:    serverTimestamp(),
        });
      }
      onSuccess();
    } catch (err) {
      console.error("Review error:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm animate-fade-in">
        {/* Header */}
        <div className="px-6 py-5 border-b border-surface-100 flex items-start justify-between">
          <div>
            <h2 className="font-semibold text-ink-900 text-lg">
              {isEdit ? "Edit Your Review" : "Leave a Review"}
            </h2>
            <p className="text-ink-400 text-xs font-sans mt-0.5">
              {booking.service?.name} · with {booking.barber?.name}
            </p>
          </div>
          <button onClick={onClose} className="text-ink-300 hover:text-ink-600 transition-colors mt-0.5">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Star picker */}
          <div>
            <p className="form-label mb-2">Your rating</p>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  className="transition-transform hover:scale-110 focus:outline-none"
                >
                  <Star
                    size={28}
                    className={`transition-colors ${
                      star <= (hover || rating)
                        ? "text-amber-400 fill-amber-400"
                        : "text-surface-200 fill-surface-200"
                    }`}
                  />
                </button>
              ))}
              {rating > 0 && (
                <span className="ml-2 text-sm font-mono text-ink-500">
                  {["", "Poor", "Fair", "Good", "Great", "Excellent"][rating]}
                </span>
              )}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="form-label">Comment <span className="text-ink-300 font-normal">(optional)</span></label>
            <textarea
              className="form-input resize-none"
              rows={3}
              placeholder="Share your experience..."
              value={comment}
              onChange={e => setComment(e.target.value)}
              maxLength={500}
            />
            <p className="text-ink-300 text-xs font-mono mt-1 text-right">{comment.length}/500</p>
          </div>

          {error && <p className="text-xs text-red-600 font-sans">{error}</p>}

          <button
            type="submit"
            disabled={saving || rating === 0}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {saving
              ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              : <><Star size={15} /> {isEdit ? "Update Review" : "Submit Review"}</>
            }
          </button>
        </form>
      </div>
    </div>
  );
}
